import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { ToastController } from '@ionic/angular';
import {
  Action,
  Actions,
  createSelector,
  ofActionSuccessful,
  Selector,
  State,
  StateContext,
  Store
} from '@ngxs/store';
import {
  catchError,
  defer,
  EMPTY,
  retry,
  switchMap,
  take,
  tap,
  throwError
} from 'rxjs';
import { produce } from 'immer';
import { TranslocoService } from '@ngneat/transloco';

import {
  CardTerminalTypes,
  CreditCardProcessingType,
  PaymentRequestType,
  TicketStatus,
  ServerCustomError,
  PaymentResult
} from 'src/app/models';
import { AppSelectors } from 'src/app/store/app/app.selectors';
import { PaymentService, TicketService } from 'src/app/services';
import { GlobalService } from 'src/app/services/global/global.service';
import { ExceptionError, HttpRequestError } from '../error/error.action';
import {
  LoadTicketDetailSuccess,
  MealPlanPaymentError,
  RemoveTicketSuccess,
  UpdateTicketSuccess
} from '../tickets/tickets.action';
import {
  CancelGatewayTransaction,
  CancelGatewayTransactionSuccess,
  FinalizeMealPlanTicket,
  FinalizeMealPlanTicketError,
  FinalizeMealPlanTicketSuccess,
  GetBusinessLines,
  GetDepartments,
  GetGiftCard,
  GetGiftCardFailed,
  GetGiftCardMultiMatch,
  GetGiftCardSuccess,
  GetTopUpGiftCard,
  GetTopUpGiftCardMultiMatch,
  GetTopUpGiftCardNoMatch,
  GetTopUpGiftCardSuccess,
  MakePayment,
  MakePaymentError,
  MakePaymentFailed,
  MakePaymentGatewayTransactionSuccess,
  MakePaymentSuccess,
  OnGatewayTransactionError,
  OnGatewayTransactionSuccess,
  OnRetryGatewayTransaction,
  PayOnPickup,
  PayOnPickupError,
  PayOnPickupSuccess,
  PollGatewayTransaction,
  ResetDepartments
} from './payment.action';
import { LOG_ROCKET_CUSTOM_EVENTS, LogRocketProvider } from 'src/app/providers';
import { TICKETS_STATE_TOKEN } from 'src/app/store/tickets/tickets.state';
import { PAYMENT_STATE_TOKEN, PaymentStateModel } from './payment.state.model';
import { LOCATION_STATE_TOKEN } from 'src/app/store/location/location.state';
import { StartIdleTime, StopIdle } from 'src/app/store/app/app.action';
import { ToastService } from 'src/app/services/toast/toast.service';

const GATEWAY_TRANSACTION_POLL_INTERVAL_MS = 2000;

@State({
  name: PAYMENT_STATE_TOKEN,
  defaults: {
    departments: [],
    businessLines: []
  }
})
@Injectable()
export class PaymentState {
  isCancelingTransaction = false;
  constructor(
    private readonly toastService: ToastService,
    private readonly store: Store,
    private readonly gbService: GlobalService,
    private readonly ticketService: TicketService,
    private readonly paymentService: PaymentService,
    private readonly logRocketProvider: LogRocketProvider,
    private readonly toastController: ToastController,
    private readonly ngZone: NgZone,
    private readonly action$: Actions,
    private readonly translocoService: TranslocoService
  ) {}

  @Selector()
  static businessLines(state: PaymentStateModel) {
    return state.businessLines;
  }

  @Selector()
  static departments(state: PaymentStateModel) {
    return state.departments;
  }

  @Selector()
  static lastGatewayTransaction(state: PaymentStateModel) {
    return state.lastGatewayTransaction;
  }

  static selectedGatewayCardTerminal = createSelector(
    [PaymentState.lastGatewayTransaction, AppSelectors.getCardTerminals],
    (lastGatewayTransaction, cardTerminals) => {
      const posCardTerminalId = lastGatewayTransaction?.pos_card_terminal_id;
      if (!posCardTerminalId) {
        return null;
      }

      const terminal = cardTerminals.find((t) => t.id === posCardTerminalId);
      return terminal;
    }
  );

  static isGatewayTransactionTerminalStripe = createSelector(
    [PaymentState.selectedGatewayCardTerminal],
    (selectedGatewayCardTerminal) =>
      Boolean(
        selectedGatewayCardTerminal &&
          selectedGatewayCardTerminal?.type === CardTerminalTypes.STRIPE
      )
  );

  //#region Actions

  //Call the request to get the Departments
  @Action(GetBusinessLines)
  getBusinessLines({ patchState }: StateContext<PaymentStateModel>) {
    return this.gbService
      .getBusinessLines()
      .pipe(tap((businessLines) => patchState({ businessLines })));
  }

  //Call the request to get the Departments
  @Action(GetDepartments)
  getDepartments(
    { patchState }: StateContext<PaymentStateModel>,
    { search_text, business_line_id }: GetDepartments
  ) {
    return this.gbService
      .getDepartments(search_text, business_line_id)
      .pipe(tap((departments) => patchState({ departments })));
  }

  @Action(ResetDepartments)
  resetDepartments({ patchState }: StateContext<PaymentStateModel>) {
    patchState({ departments: [] });
  }

  //Call the request to make the payment
  @Action(MakePayment)
  makePayment(
    { dispatch, setState }: StateContext<PaymentStateModel>,
    { ticket, payment_request, closed_from_source }: MakePayment
  ) {
    if (ticket) {
      const isCreditCardProcessing =
        payment_request.type === PaymentRequestType.CREDIT_CARD &&
        this.store.selectSnapshot(LOCATION_STATE_TOKEN).location
          .credit_card_processing !== CreditCardProcessingType.None;

      if (isCreditCardProcessing) {
        dispatch(new StopIdle());
        this.action$
          .pipe(
            ofActionSuccessful(
              MakePaymentFailed,
              OnGatewayTransactionSuccess,
              OnGatewayTransactionError
            ),
            take(1)
          )
          .subscribe(() => dispatch(new StartIdleTime()));

        return this.ticketService
          .makePaymentGatewayTransaction({
            ticket_uuid: ticket.device_ticket_uuid,
            paymentRequest: payment_request,
            closed_from_source
          })
          .pipe(
            tap((result) => {
              const gateway_transaction_id = result?.gateway_transaction_id;
              if (gateway_transaction_id) {
                setState(
                  produce((draft) => {
                    draft.lastGatewayTransaction = {
                      ...draft.lastGatewayTransaction,
                      ...result
                    };
                  })
                );
                dispatch(
                  new MakePaymentGatewayTransactionSuccess(
                    gateway_transaction_id,
                    ticket,
                    payment_request,
                    result?.pos_card_terminal_id,
                    result?.collect_manual_card_entry_on_pinpad
                  )
                );
              }
            }),
            catchError((res) => {
              const error = res.error as ServerCustomError;
              if (!error) {
                return EMPTY;
              }
              const pending_transaction_id = Number(
                error.details?.pending_transaction_id
              );
              if (
                error.status !== 'payment_in_progress' ||
                !pending_transaction_id
              ) {
                dispatch(
                  new MakePaymentFailed(error, { ticket, payment_request })
                );
                return EMPTY;
              }

              return this.ticketService
                .getTrancloudTransaction(
                  ticket.device_ticket_uuid,
                  pending_transaction_id
                )
                .pipe(
                  tap((pendingTx) => {
                    const selectedTerminalId =
                      payment_request.payment_terminal_id;
                    const pendingTerminalId = pendingTx?.pos_card_terminal_id;
                    const terminalsDiffer =
                      selectedTerminalId &&
                      pendingTerminalId &&
                      selectedTerminalId !== pendingTerminalId;

                    if (terminalsDiffer) {
                      const cardTerminals = this.store.selectSnapshot(
                        AppSelectors.getCardTerminals
                      );
                      const pendingTerminalName =
                        cardTerminals.find((t) => t.id === pendingTerminalId)
                          ?.name ?? '';
                      this.toastService.warn(
                        this.translocoService.translate(
                          'payment.terminalMismatchWarningMessage',
                          { terminalName: pendingTerminalName }
                        )
                      );
                    }

                    dispatch(
                      new MakePaymentGatewayTransactionSuccess(
                        pending_transaction_id,
                        ticket,
                        payment_request,
                        pendingTerminalId,
                        pendingTx.collect_manual_card_entry_on_pinpad
                      )
                    );
                  }),
                  catchError(
                    () =>
                      // dispatch(
                      //   new MakePaymentGatewayTransactionSuccess(
                      //     pending_transaction_id,
                      //     ticket,
                      //     payment_request
                      //   )
                      // );
                      EMPTY
                  )
                );
            })
          );
      }

      return this.ticketService
        .makePaymentTransactions({
          payment_request,
          closed_from_source,
          ticket_uuid: ticket.device_ticket_uuid
        })
        .pipe(
          tap((response) => {
            const responseTicket = response.ticket;
            const actions: unknown[] = [
              new MakePaymentSuccess(
                ticket.device_ticket_uuid,
                response,
                payment_request
              )
            ];

            this.logRocketProvider.track(
              LOG_ROCKET_CUSTOM_EVENTS.Transaction.Complete,
              {
                device_ticket_uuid: ticket.device_ticket_uuid,
                type: payment_request.type
              }
            );

            if (
              responseTicket &&
              responseTicket.status !== TicketStatus.CLOSED
            ) {
              actions.push(new LoadTicketDetailSuccess(responseTicket));
            }

            dispatch(actions);
          }),
          catchError((res) => {
            const error = res.error;
            if (error) {
              dispatch(
                new MakePaymentFailed(error, { ticket, payment_request })
              );
            }
            return EMPTY;
          })
        );
    }
  }

  @Action(MakePaymentGatewayTransactionSuccess)
  makePaymentGatewayTransactionSuccess(
    { patchState, dispatch }: StateContext<PaymentStateModel>,
    {
      gateway_transaction_id,
      ticket,
      paymentRequest,
      pos_card_terminal_id
    }: MakePaymentGatewayTransactionSuccess
  ) {
    this.logRocketProvider.track(
      LOG_ROCKET_CUSTOM_EVENTS.Transaction.InProgress,
      {
        device_ticket_uuid: ticket.device_ticket_uuid,
        type: paymentRequest.type
      }
    );

    patchState({
      lastGatewayTransaction: {
        ticket,
        paymentRequest,
        pending_gateway_transaction_id: gateway_transaction_id,
        pos_card_terminal_id
      }
    });

    dispatch(
      new PollGatewayTransaction(gateway_transaction_id, ticket, paymentRequest)
    );
  }

  /**
   * Polls the gateway transaction status until completion or cancellation.
   *
   * @param context - The NGXS state context providing patchState, dispatch, and setState operations.
   * @param action - The action payload containing gateway_transaction_id, ticket, and paymentRequest.
   * @returns An observable emitting the transaction response or completing without emission if canceled.
   */
  @Action(PollGatewayTransaction)
  pollGatewayTransaction(
    { patchState, dispatch, setState }: StateContext<PaymentStateModel>,
    { gateway_transaction_id, ticket, paymentRequest }: PollGatewayTransaction
  ) {
    this.isCancelingTransaction = false;
    return defer(() => {
      if (this.isCancelingTransaction) {
        return EMPTY;
      }

      return this.ticketService.getTrancloudTransaction(
        ticket.device_ticket_uuid,
        gateway_transaction_id
      );
    }).pipe(
      tap((response) => {
        switch (response.status) {
          case 'in_progress':
            setState(
              produce((draft) => {
                if (draft.lastGatewayTransaction) {
                  draft.lastGatewayTransaction.stripe_payment_secret =
                    response.stripe_payment_secret;
                  draft.lastGatewayTransaction.stripe_pk = response.stripe_pk;
                  draft.lastGatewayTransaction.stripe_account_id =
                    response.stripe_account_id;
                  draft.lastGatewayTransaction.pos_card_terminal_id =
                    response.pos_card_terminal_id;
                  draft.lastGatewayTransaction.collect_manual_card_entry_on_pinpad =
                    response.collect_manual_card_entry_on_pinpad;
                }
              })
            );
            throw response;
          case 'complete': {
            const { transaction, new_balance, ticket_status, tip_amount } =
              response;
            const paymentResult: PaymentResult = {
              transaction,
              new_balance,
              ticket_status,
              tip_amount
            };

            this.logRocketProvider.track(
              LOG_ROCKET_CUSTOM_EVENTS.Transaction.Complete,
              {
                device_ticket_uuid: ticket.device_ticket_uuid,
                transaction_id: transaction?.id,
                type: transaction?.type
              }
            );

            dispatch([
              new OnGatewayTransactionSuccess(),
              new MakePaymentSuccess(
                ticket.device_ticket_uuid,
                paymentResult,
                paymentRequest
              )
            ]);
            break;
          }
          case 'error':
          default: {
            this.logRocketProvider.track(
              LOG_ROCKET_CUSTOM_EVENTS.Transaction.Declined,
              {
                device_ticket_uuid: ticket.device_ticket_uuid
              }
            );

            //Default Gateway Transaction error
            let error = this.translocoService.translate(
              'errors.gatewayTransactionProblem'
            );

            // Gateway Transaction payment_gateway_response value will be different depending on the payment processor:
            // Stripe - String value returned with readable error
            // Trancloud - Object returned, readable error within Rstream.TextResponse
            const payment_gateway_response = response?.payment_gateway_response;
            const RStream =
              payment_gateway_response?.RStream ||
              payment_gateway_response?.Rstream;
            const CmdStatus = RStream?.CmdStatus;
            const TextResponse = RStream?.TextResponse;

            if (typeof payment_gateway_response === 'string') {
              error = payment_gateway_response;
            } else if (TextResponse) {
              error = TextResponse;
            }
            patchState({
              lastGatewayTransaction: {
                ticket,
                paymentRequest
              }
            });
            dispatch(new OnGatewayTransactionError(error, CmdStatus));
          }
        }
      }),
      catchError((e) => {
        //Action should only be dispatched if there is an actual backend error response.
        //This catchError is triggered when response status is in_progress due to the throw on the above Switch Case to trigger the retry operator.
        if (e instanceof HttpErrorResponse && e.error?.message === 'failed') {
          dispatch(
            new OnGatewayTransactionError(
              this.translocoService.translate(
                'errors.gatewayTransactionCancelled'
              )
            )
          );
          return EMPTY;
        }

        return throwError(() => e);
      }),
      retry({ delay: GATEWAY_TRANSACTION_POLL_INTERVAL_MS })
    );
  }

  @Action(MakePaymentFailed)
  makePaymentFailed(
    ctx: StateContext<PaymentStateModel>,
    { error, paymentData }: MakePaymentFailed
  ) {
    const isCreditCard =
      paymentData.payment_request.type === PaymentRequestType.CREDIT_CARD;
    if (isCreditCard) {
      ctx.patchState({
        lastGatewayTransaction: {
          ticket: paymentData.ticket,
          paymentRequest: paymentData.payment_request
        }
      });
    }

    const errorMessage: string = error.message;
    const errorAction = errorMessage.includes('Insufficient mealplan')
      ? new MealPlanPaymentError(
          paymentData.ticket.device_ticket_uuid,
          errorMessage
        )
      : new MakePaymentError(error, paymentData);
    ctx.dispatch(errorAction);
  }

  @Action(MakePaymentError)
  makePaymentError(
    ctx: StateContext<PaymentStateModel>,
    { error, posPaymentData }: MakePaymentError
  ) {
    const paymentRequest = posPaymentData.payment_request;
    const httpErrorMsg = error.message;
    let errorMessage =
      httpErrorMsg ||
      this.translocoService.translate('errors.paymentProcessingError');

    if (paymentRequest.type === PaymentRequestType.CHARGE_ACCOUNT) {
      if (error.status === '422') {
        errorMessage = this.translocoService.translate(
          'errors.insufficientFunds',
          { errorMsg: httpErrorMsg }
        );
      }
    }

    ctx.dispatch(new HttpRequestError(errorMessage));
  }

  /**
   * Action to retry a payment gateway transaction.
   */
  @Action(OnRetryGatewayTransaction)
  retryGatewayTransaction(ctx: StateContext<PaymentStateModel>) {
    const { lastGatewayTransaction } = ctx.getState();

    const selectedTicketId =
      this.store.selectSnapshot(TICKETS_STATE_TOKEN).selectedTicket?.data;

    if (!selectedTicketId || !lastGatewayTransaction) {
      ctx.dispatch(
        new ExceptionError(
          this.translocoService.translate('errors.paymentProcessingError')
        )
      );
      return null;
    }

    this.logRocketProvider.track(LOG_ROCKET_CUSTOM_EVENTS.Transaction.Retry, {
      device_ticket_uuid: selectedTicketId,
      existing_pgt_id: lastGatewayTransaction.result?.gateway_transaction_id
    });

    // If we have an existing PGT result, check its status before retrying
    if (lastGatewayTransaction.result?.gateway_transaction_id) {
      return this.ticketService
        .getTrancloudTransaction(
          selectedTicketId,
          lastGatewayTransaction.result.gateway_transaction_id
        )
        .pipe(
          switchMap((response) => {
            if (response.status === 'complete') {
              if (!response.transaction || !response.ticket_status) {
                return ctx.dispatch(
                  new OnGatewayTransactionError(
                    this.translocoService.translate(
                      'errors.paymentProcessingError'
                    )
                  )
                );
              }
              // Payment already went through - dispatch success
              return ctx.dispatch([
                new OnGatewayTransactionSuccess(),
                new MakePaymentSuccess(
                  selectedTicketId,
                  {
                    transaction: response.transaction,
                    ticket_status: response.ticket_status,
                    new_balance: response.new_balance as string,
                    tip_amount: response.tip_amount
                  },
                  lastGatewayTransaction.paymentRequest
                )
              ]);
            }

            if (response.status === 'error' || response.status === 'declined') {
              // PGT is confirmed dead - safe to retry
              return ctx.dispatch(
                new MakePayment(
                  lastGatewayTransaction.ticket,
                  lastGatewayTransaction.paymentRequest
                )
              );
            }

            // Still in progress or unknown status - retry anyway,
            // backend guard (Phase 6) handles dedup
            return ctx.dispatch(
              new MakePayment(
                lastGatewayTransaction.ticket,
                lastGatewayTransaction.paymentRequest
              )
            );
          }),
          catchError(() =>
            // Network error checking PGT - fall through to MakePayment,
            // backend guard (Phase 6) handles dedup
            ctx.dispatch(
              new MakePayment(
                lastGatewayTransaction.ticket,
                lastGatewayTransaction.paymentRequest
              )
            )
          )
        );
    }

    // No existing PGT result to check - just retry
    return ctx.dispatch(
      new MakePayment(
        lastGatewayTransaction.ticket,
        lastGatewayTransaction.paymentRequest
      )
    );
  }

  @Action(OnGatewayTransactionSuccess)
  onGatewayTransactionSuccess({ patchState }: StateContext<PaymentStateModel>) {
    patchState({ lastGatewayTransaction: undefined });
  }

  @Action(GetGiftCard)
  getGiftCard(
    ctx: StateContext<PaymentStateModel>,
    { identification_number }: GetGiftCard
  ) {
    return this.gbService.getGiftCards(identification_number).pipe(
      tap((cards) => {
        if (cards.length > 1) {
          ctx.dispatch(new GetGiftCardMultiMatch(cards));
        } else if (cards.length === 1) {
          ctx.dispatch(new GetGiftCardSuccess(cards[0]));
        } else {
          ctx.dispatch(new GetGiftCardFailed());
        }
      })
    );
  }

  // POS-1742: same number can exist on more than one card; scope the match
  // to the operator's current facility so a top-up can't silently land on a
  // card issued elsewhere.
  @Action(GetTopUpGiftCard)
  getTopUpGiftCard(
    ctx: StateContext<PaymentStateModel>,
    { identification_number }: GetTopUpGiftCard
  ) {
    return this.gbService.getGiftCards(identification_number).pipe(
      tap((cards) => {
        const facilityId = this.store.selectSnapshot(
          AppSelectors.currentFacility
        );
        const matches = (cards || []).filter(
          (card) => card.issued_at_facility_id === facilityId
        );

        if (matches.length > 1) {
          ctx.dispatch(new GetTopUpGiftCardMultiMatch(matches));
        } else if (matches.length === 1) {
          ctx.dispatch(new GetTopUpGiftCardSuccess(matches[0]));
        } else {
          ctx.dispatch(new GetTopUpGiftCardNoMatch());
        }
      })
    );
  }

  @Action(CancelGatewayTransaction)
  onCancelGatewayTransaction({
    getState,
    dispatch
  }: StateContext<PaymentStateModel>) {
    this.isCancelingTransaction = true;
    const lastGatewayTransaction = getState().lastGatewayTransaction;

    if (!lastGatewayTransaction) {
      return null;
    }

    const { ticket, result } = lastGatewayTransaction;
    const gateway_transaction_id =
      result?.gateway_transaction_id ||
      lastGatewayTransaction.pending_gateway_transaction_id;

    return this.paymentService
      .cancelGatewayTransaction(
        ticket.device_ticket_uuid,
        gateway_transaction_id
      )
      .pipe(
        tap(({ success, error_message, payment_received }) => {
          if (success) {
            dispatch(new CancelGatewayTransactionSuccess());
            return;
          }

          if (error_message) {
            this.toastService.error(error_message);
          }

          if (payment_received) {
            dispatch(
              new PollGatewayTransaction(
                gateway_transaction_id,
                lastGatewayTransaction.ticket,
                lastGatewayTransaction.paymentRequest
              )
            );
            // setState(
            //   produce((draft) => {
            //     if (draft.lastGatewayTransaction) {
            //       draft.lastGatewayTransaction.pending_gateway_transaction_id =
            //         gateway_transaction_id;
            //     }
            //   })
            // );
          }
        }),
        catchError((error: HttpErrorResponse) => {
          // setState(
          //   produce((draft) => {
          //     if (draft.lastGatewayTransaction) {
          //       draft.lastGatewayTransaction.pending_gateway_transaction_id =
          //         gateway_transaction_id;
          //     }
          //   })
          // );

          const serverMessage =
            (error?.error as ServerCustomError)?.message ||
            error?.error?.error_message;
          this.toastService.error(
            serverMessage ||
              this.translocoService.translate(
                'errors.cancelGatewayTransactionProblem'
              )
          );

          return EMPTY;
        })
      );
  }

  @Action(PayOnPickup)
  payOnPickup(
    ctx: StateContext<PaymentStateModel>,
    { deviceTicketUuid }: PayOnPickup
  ) {
    return this.paymentService.payOnPickup(deviceTicketUuid).pipe(
      tap((res) => ctx.dispatch(new PayOnPickupSuccess(res))),
      catchError(() =>
        ctx.dispatch(
          new PayOnPickupError(
            this.translocoService.translate('errors.problemPlacingOrder')
          )
        )
      )
    );
  }

  @Action(PayOnPickupSuccess)
  payOnPickupSuccess(
    ctx: StateContext<PaymentStateModel>,
    { ticket }: PayOnPickupSuccess
  ) {
    const isAutoCloseTicket =
      this.store.selectSnapshot(LOCATION_STATE_TOKEN).location
        .auto_close_on_full_payment;

    this.toastPlaceOrderSuccess();

    if (isAutoCloseTicket) {
      return ctx.dispatch(new RemoveTicketSuccess(ticket.device_ticket_uuid));
    }

    return ctx.dispatch(new UpdateTicketSuccess(ticket));
  }

  @Action(FinalizeMealPlanTicket)
  finalizeMealPlanTicket(
    { dispatch }: StateContext<PaymentStateModel>,
    { deviceTicketUuid, tipAmount }: FinalizeMealPlanTicket
  ) {
    return this.paymentService
      .finalizeMealPlanTicket(deviceTicketUuid, tipAmount)
      .pipe(
        switchMap((res) =>
          res.success
            ? dispatch(new FinalizeMealPlanTicketSuccess(res.ticket))
            : dispatch(
                new FinalizeMealPlanTicketError(
                  this.translocoService.translate('errors.problemPlacingOrder')
                )
              )
        ),
        catchError((error: HttpErrorResponse) =>
          dispatch(
            new FinalizeMealPlanTicketError(
              this.translocoService.translate('errors.problemPlacingOrder'),
              error
            )
          )
        )
      );
  }

  @Action(FinalizeMealPlanTicketSuccess)
  finalizeMealPlanTicketSuccess(
    { dispatch }: StateContext<PaymentStateModel>,
    { ticket }: FinalizeMealPlanTicketSuccess
  ) {
    return dispatch(
      new RemoveTicketSuccess(ticket.device_ticket_uuid)
    ).subscribe(() => this.toastPlaceOrderSuccess());
  }
  //#endregion

  private toastPlaceOrderSuccess() {
    this.ngZone.run(async () => {
      const toast = await this.toastController.create({
        color: 'success',
        message: this.translocoService.translate(
          'messages.orderPlacedSuccessfully'
        ),
        duration: 4000
      });
      await toast.present();
    });
  }
}
